<script lang="ts">
  import Breadcrumb from '../shared/Breadcrumb.svelte';
  import EnvSwitcher from '../shared/EnvSwitcher.svelte';
  import { fetchLogs, type LogGroup, type TimeRange, type LogEntry } from '../../services/logs-client';

  let env = $state<'Beta' | 'Prod'>('Beta');
  let logGroup = $state<LogGroup>('cs-proxy');
  let timeRange = $state<TimeRange>('1h');
  let search = $state('');
  let entries = $state<LogEntry[]>([]);
  let loading = $state(false);
  let queried = $state(false);

  const groups: { value: LogGroup; label: string }[] = [
    { value: 'cs-proxy', label: 'CS Proxy' },
    { value: 'cs-worker', label: 'CS Worker' },
    { value: 'cs3-service', label: 'CS3 Service' },
    { value: 'bedrock-agentcore', label: 'CS Agent (AgentCore)' },
    { value: 'athena-query', label: 'Athena Query' },
    { value: 'estimator', label: 'Estimator' },
  ];

  const ranges: { value: TimeRange; label: string }[] = [
    { value: '1h', label: 'Last 1h' },
    { value: '6h', label: 'Last 6h' },
    { value: '24h', label: 'Last 24h' },
    { value: '7d', label: 'Last 7d' },
  ];

  const levelStyles: Record<string, string> = {
    INFO: 'bg-info/15 text-info border-info/30',
    WARN: 'bg-warning/15 text-warning border-warning/30',
    ERROR: 'bg-error/15 text-error border-error/30',
    DEBUG: 'bg-text-muted/15 text-text-muted border-text-muted/30',
  };

  async function loadLogs() {
    loading = true;
    queried = true;
    try {
      entries = await fetchLogs(logGroup, timeRange, search, env);
    } finally {
      loading = false;
    }
  }

  function formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
  }
</script>

<div class="min-h-screen bg-bg-dark text-text-primary">
  <div class="w-full px-6 py-4">
    <div class="flex items-center justify-between pr-20">
      <Breadcrumb items={[{ label: 'Log Explorer' }]} />
      <EnvSwitcher bind:value={env} />
    </div>

    <div class="mt-4 mb-6">
      <h1 class="text-2xl font-bold">📜 Log Explorer</h1>
      <p class="mt-1 text-sm text-text-secondary">Search and filter CloudWatch logs across all services</p>
    </div>

    <!-- Controls -->
    <div class="bg-bg-panel border border-border rounded-xl p-5 mb-6">
      <div class="flex flex-wrap items-end gap-4">
        <div>
          <label for="log-group" class="block text-xs text-text-secondary mb-1.5">Log Group</label>
          <select
            id="log-group"
            bind:value={logGroup}
            class="bg-bg-input border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-accent"
          >
            {#each groups as g}
              <option value={g.value}>{g.label}</option>
            {/each}
          </select>
        </div>

        <div class="flex gap-1 items-end" role="radiogroup" aria-label="Time range">
          {#each ranges as r}
            <button
              onclick={() => timeRange = r.value}
              class="px-3 py-2 rounded-lg text-xs font-medium transition-colors {timeRange === r.value ? 'bg-accent text-white' : 'bg-bg-input border border-border text-text-secondary hover:text-text-primary'}"
              role="radio"
              aria-checked={timeRange === r.value}
            >
              {r.label}
            </button>
          {/each}
        </div>

        <div class="flex-1 min-w-[200px]">
          <label for="log-search" class="block text-xs text-text-secondary mb-1.5">Search</label>
          <input
            id="log-search"
            type="text"
            bind:value={search}
            placeholder="Filter log messages…"
            class="w-full bg-bg-input border border-border rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
          />
        </div>

        <button
          onclick={loadLogs}
          disabled={loading}
          class="px-5 py-2 bg-accent hover:bg-accent-hover text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Fetch Logs'}
        </button>
        {#if entries.length > 0}
          <button
            onclick={() => { entries = []; queried = false; }}
            class="px-4 py-2 bg-bg-input border border-border hover:border-error/50 text-text-secondary hover:text-error rounded-lg text-sm font-medium transition-colors"
          >
            Clear
          </button>
        {/if}
      </div>
    </div>

    <!-- Log Entries -->
    {#if loading}
      <div class="space-y-1">
        {#each Array(8) as _, i (i)}
          <div class="h-8 rounded bg-bg-panel animate-skeleton-pulse" style="width: {70 + Math.random() * 30}%"></div>
        {/each}
      </div>
    {:else if queried && entries.length === 0}
      <div class="bg-bg-panel border border-border rounded-xl p-12 text-center">
        <p class="text-4xl mb-3">📭</p>
        <p class="text-text-secondary">No log entries found</p>
      </div>
    {:else if entries.length > 0}
      <div class="mb-3 text-xs text-text-secondary">{entries.length} entries</div>
      <div class="bg-bg-panel border border-border rounded-xl overflow-hidden">
        <div class="overflow-auto max-h-[calc(100vh-320px)]">
          {#each entries as entry, i (i)}
            <div class="flex items-start gap-3 px-4 py-2 border-b border-border-light hover:bg-bg-hover transition-colors font-mono text-xs {i % 2 === 1 ? 'bg-bg-dark/20' : ''}">
              <span class="text-text-muted shrink-0 w-[90px]">{formatTime(entry.timestamp)}</span>
              <span class="shrink-0">
                <span class="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold border {levelStyles[entry.level]}">{entry.level}</span>
              </span>
              <span class="text-text-primary break-all">{entry.message}</span>
              <span class="text-text-muted shrink-0 ml-auto">{entry.requestId}</span>
            </div>
          {/each}
        </div>
      </div>
    {/if}
  </div>
</div>
